import {
    Avatar, AvatarProps, Divider, Table, TableCell, TableContainer, TableRow, TableRowProps, Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    BorderColorDark, BorderColorLight, DarkModeBackground,
    MUIGrey,
    PaperBorderRadius, PrimaryThemeColorLight, WhiteColor
} from 'shared/SharedStyles/styleConstants';

type ColorType = 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'transparent';

interface IShAvatarProps extends AvatarProps {
    color: ColorType,
    height?: number,
    width?: number
}

export const ShAvatar = styled((props: IShAvatarProps) => <Avatar {...props} />)(
    ({ theme, color, width, height }) => ({
        backgroundColor: color === 'primary' ? theme.palette.primary.main :
            color === 'secondary' ? theme.palette.secondary.main : color === 'success' ?
                theme.palette.success.main : color === 'warning' ? theme.palette.warning.main : color === 'transparent' ?
                    'transparent' : theme.palette.error.main,

        height: height ?? 'unset',
        width: width ?? 'unset'
    })
);

export const ThemeColorDivider = styled(Divider)(({ theme }) => ({
    borderColor: theme.palette.mode === 'light' ? BorderColorLight : BorderColorDark
}));


// Table view styles
export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    overflow: 'auto',
    flex: 1,
    border: `1px solid ${theme.palette.mode === 'light' ? BorderColorLight : BorderColorDark}`,
    borderRadius: PaperBorderRadius
}));

export const StyledTable = styled(Table)(({ theme }) => ({
    borderCollapse: 'separate',
    [theme.breakpoints.up('xs')]: {
        [`& td,th`]: {
            padding: '2px'
        },
    },
    [theme.breakpoints.up('sm')]: {
        [`& td,th`]: {
            padding: '4px 8px'
        },
    },
    [theme.breakpoints.up('md')]: {
        [`& td,th`]: {
            padding: '6px 10px'
        },
    },
    [theme.breakpoints.up('lg')]: {
        [`& td,th`]: {
            padding: '6px 14px'
        },
    },
    [theme.breakpoints.up('xl')]: {
        [`& td,th`]: {
            padding: '6px 16px'
        },
    },
    '& tr': {
        '& td,th': {
            borderBottom: `1px solid ${theme.palette.mode === 'light' ? BorderColorLight : BorderColorDark}`,
        }
    }
}));

interface IStyledTableRow extends TableRowProps {
    cursor: "pointer" | "default";
}

export const StyledTableRow = styled((props: IStyledTableRow) => <TableRow {...props} />)(
    ({ theme, cursor }) => ({
        cursor: cursor,
        backgroundColor: theme.palette.mode === 'light' ? WhiteColor : DarkModeBackground,
        '& th': {
            fontWeight: 500,
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: theme.palette.mode === 'light' ? WhiteColor : DarkModeBackground,
            '&.select-header': {
                left: 0,
                zIndex: 3
            }
        },
        '& .more-btn': {
            opacity: 0,
        },
        '&:hover': {
            '& .on-hover': {
                visibility: 'visible',
            },
            '& .more-btn': {
                opacity: 1,
            }
        },
        '& .on-hover': {
            visibility: 'hidden', // Initially hidden
        },
        '&:hover .on-hover': {
            visibility: 'visible', // Visible on hover
        },
        '&.is-actions-open': {
            backgroundColor: PrimaryThemeColorLight
        }
    })
);

export const RowSelectCell = styled(TableCell)(({ theme }) => ({
    width: '2%',
    paddingRight: '0px',
    textAlign: 'center',
    position: 'sticky',
    left: 0,
    zIndex: 1,
    backgroundColor: 'inherit'
}));

export const CollapsibleTableRow = styled(TableRow)(
    ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'light' ? WhiteColor : DarkModeBackground,
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'table-row',
        },
        [theme.breakpoints.up('md')]: {
            display: 'table-row',
        },
        '& .MuiTableCell-root': {
            padding: 0,
            border: 'none'
        }
    })
);

export const UnderlinedText = styled(Typography)(() => (
    {
        textDecoration: 'underline'
    }
));

export const GutterText = styled(Typography)(({ theme }) => (
    {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    }
));

export const LabelWithBadge = styled(Typography)(
    ({ theme }) => ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '& .MuiTypography-caption': {
            backgroundColor: theme.palette.getContrastText(theme.palette.primary.main),
            color: theme.palette.primary.main,
            padding: theme.spacing(0.5),
            height: '20px',
            minWidth: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '20px'
        }
    })
);

export const ShBullets = styled('ul')(() => (
    {
        paddingInlineStart: '20px',
        color: MUIGrey,
        margin: 'unset'
    }
));
